import { useState } from 'react'
import { Bell, Shield, Globe, Save } from 'lucide-react'
import { cn } from '../lib/utils'

export default function SettingsPage() {
 const [activeTab, setActiveTab] = useState<'general' | 'notifications' | 'security'>('general')
 const [saved, setSaved] = useState(false)

 const handleSave = () => {
 setSaved(true)
 setTimeout(() => setSaved(false), 2000)
 }

 const tabs = [
 { id: 'general' as const, label: 'Geral', icon: Globe },
 { id: 'notifications' as const, label: 'Notificações', icon: Bell },
 { id: 'security' as const, label: 'Segurança', icon: Shield },
 ]

 return (
 <div className="space-y-6">
 <div>
 <h1 className="font-serif text-2xl font-semibold text-foreground">Configurações</h1>
 <p className="mt-1 text-sm text-muted-foreground">Gerencie as preferências do sistema</p>
 </div>

 {/* Tabs */}
 <div className="border-b border-border">
 <div className="flex gap-4 overflow-x-auto">
 {tabs.map((tab) => (
 <button
 key={tab.id}
 onClick={() => setActiveTab(tab.id)}
 className={cn(
 "flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors touch-target",
 activeTab === tab.id
 ? "border-primary text-primary"
 : "border-transparent text-muted-foreground hover:text-foreground"
 )}
 >
 <tab.icon className="h-4 w-4" />
 {tab.label}
 </button>
 ))}
 </div>
 </div>

 {/* General Settings */}
 {activeTab === 'general' && (
 <div className="space-y-6 rounded-lg border border-border bg-card p-6">
 <div className="space-y-4">
 <div className="space-y-2">
 <label htmlFor="firm-name" className="text-sm font-medium text-foreground">
 Nome do Escritório
 </label>
 <input
 id="firm-name"
 type="text"
 defaultValue="Silva & Associados"
 className="flex h-11 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 touch-target"
 />
 </div>
 <div className="space-y-2">
 <label htmlFor="timezone" className="text-sm font-medium text-foreground">
 Fuso Horário
 </label>
 <select
 id="timezone"
 defaultValue="America/Sao_Paulo"
 className="flex h-11 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 touch-target"
 >
 <option value="America/Sao_Paulo">Brasília (GMT-3)</option>
 <option value="America/Manaus">Manaus (GMT-4)</option>
 <option value="America/Noronha">Fernando de Noronha (GMT-2)</option>
 </select>
 </div>
 <div className="space-y-2">
 <label htmlFor="language" className="text-sm font-medium text-foreground">
 Idioma
 </label>
 <select
 id="language"
 defaultValue="pt-BR"
 className="flex h-11 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 touch-target"
 >
 <option value="pt-BR">Português (Brasil)</option>
 <option value="en-US">English (US)</option>
 </select>
 </div>
 </div>
 </div>
 )}

 {/* Notifications Settings */}
 {activeTab === 'notifications' && (
 <div className="space-y-6 rounded-lg border border-border bg-card p-6">
 <div className="space-y-4">
 {[
 { label: 'Novo lead qualificado', description: 'Receber alerta quando um lead for qualificado pelo bot', defaultChecked: true },
 { label: 'Agendamento confirmado', description: 'Notificar quando um cliente confirmar reunião', defaultChecked: true },
 { label: 'Mensagem urgente', description: 'Alertar sobre mensagens marcadas como urgentes', defaultChecked: true },
 { label: 'Resumo diário', description: 'Receber resumo das atividades às 18h', defaultChecked: false },
 ].map((item) => (
 <div key={item.label} className="flex items-start justify-between gap-4">
 <div className="space-y-1">
 <p className="text-sm font-medium text-foreground">{item.label}</p>
 <p className="text-xs text-muted-foreground">{item.description}</p>
 </div>
 <label className="relative inline-flex cursor-pointer items-center touch-target">
 <input type="checkbox" defaultChecked={item.defaultChecked} className="peer sr-only" />
 <div className="h-6 w-11 rounded-full bg-muted peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-full" />
 </label>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Security Settings */}
 {activeTab === 'security' && (
 <div className="space-y-6 rounded-lg border border-border bg-card p-6">
 <div className="space-y-4">
 <div className="space-y-2">
 <label htmlFor="current-password" className="text-sm font-medium text-foreground">
 Senha Atual
 </label>
 <input
 id="current-password"
 type="password"
 className="flex h-11 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 touch-target"
 placeholder="••••••••"
 />
 </div>
 <div className="space-y-2">
 <label htmlFor="new-password" className="text-sm font-medium text-foreground">
 Nova Senha
 </label>
 <input
 id="new-password"
 type="password"
 className="flex h-11 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 touch-target"
 placeholder="••••••••"
 />
 </div>
 <div className="space-y-2">
 <label htmlFor="confirm-password" className="text-sm font-medium text-foreground">
 Confirmar Nova Senha
 </label>
 <input
 id="confirm-password"
 type="password"
 className="flex h-11 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 touch-target"
 placeholder="••••••••"
 />
 </div>
 </div>
 </div>
 )}

 {/* Save Button */}
 <div className="flex items-center gap-3">
 <button
 onClick={handleSave}
 className="flex h-11 items-center gap-2 rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors touch-target"
 >
 <Save className="h-4 w-4" />
 Salvar Alterações
 </button>
 {saved && (
 <span className="text-sm text-primary">Salvo com sucesso!</span>
 )}
 </div>
 </div>
 )
}